import React, { useState, useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterComponent from "../TabFilter";
import TransactionTable from "./TransactionTable";
import SubTransactionTable from "./PerSettlementTransaction";
import { getSettlementTransactions, perSettlementTransactions } from "../../../../utils/api";
import config from "../../../../utils/config";
import { CsvDownApi } from "../../../../v2/integrations/export";
import { Button } from "../../../../v2/design-system";
import { theme } from "../../../../v2/design-system/theme";
import styled from "styled-components";
import { HubFlexRow, HubPageShell, HubTitleLg, HubTitleXl } from "../societyHubShell";
import { v2PaginationZeroBased } from "../tableShared";

const FilterWrap = styled.div`
  width: 20%;
  max-width: 100%;
  margin-top: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};
`;

const filterForm = [
  {
    name: "idFilter",
    type: "text",
    placeholder: "Search by Transaction ID",
    value: "",
  },
];

const Transactions = ({ orgUserDetailId }) => {
  const [showTable, setShowTable] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [subTransactions, setSubTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filters, setFilters] = useState({ idFilter: "" });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  const [txnPage, setTxnPage] = useState(0);
  const [itemsPerPageTxn, setItemsPerPageTxn] = useState(10);
  const [totalRecordsTxn, setTotalRecordsTxn] = useState(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (filters.idFilter.length >= 3 || filters.idFilter.length === 0) {
        setDebouncedFilters(filters);
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [filters]);

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const payload = {
          pageNo: page,
          pageSize: itemsPerPage,
          orgUserDetailId,
          voucherId: debouncedFilters.idFilter || null,
        };
        const res = await getSettlementTransactions(payload);
        const data = res?.data?.data;
        setTransactions(data?.content || []);
        setTotalRecords(data?.totalElements || 0);
      } catch {
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    if (showTable) {
      fetchTransactions();
    }
  }, [page, itemsPerPage, debouncedFilters, orgUserDetailId, showTable]);

  useEffect(() => {
    if (!selectedTransaction || showTable) return;

    const fetchSubTransactions = async () => {
      setLoading(true);
      try {
        const payload = {
          pageNo: txnPage,
          pageSize: itemsPerPageTxn,
          voucherId: selectedTransaction.voucherId,
        };
        const res = await perSettlementTransactions(payload);
        const data = res?.data?.data;
        setSubTransactions(data?.content || []);
        setTotalRecordsTxn(data?.totalElements || 0);
      } catch {
        setSubTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubTransactions();
  }, [txnPage, itemsPerPageTxn, selectedTransaction, showTable]);

  const handleTransactionView = (txn) => {
    setSelectedTransaction(txn);
    setShowTable(false);
    setTxnPage(0);
  };

  const handleBack = () => {
    setShowTable(true);
    setSelectedTransaction(null);
    setSubTransactions([]);
  };

  const csvHeaders = [
    { key: "createdAt", label: "Settlement Date" },
    { key: "settlementAmount", label: "Amount" },
    { key: "txnStatus", label: "Status" },
    { key: "route", label: "Route" },
    { key: "accountNo", label: "Settled To Account" },
    { key: "voucherId", label: "Transaction Id" },
  ];

  const subCsvHeaders = [
    { key: "createdAt", label: "Date" },
    { key: "transactionAmount", label: "Amount" },
    { key: "txnMode", label: "Transaction Mode" },
    { key: "voucherId", label: "Transaction Id" },
    { key: "txnStatus", label: "Status" },
    { key: "narration", label: "Narration" },
  ];

  return (
    <HubPageShell>
      <HubFlexRow $mb={theme.spacing.md}>
        {!showTable && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            leftIcon={<ArrowBackIcon sx={{ fontSize: 20 }} />}
            onClick={handleBack}
          >
            Back to Transactions
          </Button>
        )}
      </HubFlexRow>

      {showTable ? (
        <>
          <HubFlexRow $justify="space-between" $mb={theme.spacing.md}>
            <HubTitleXl>Settlement Transactions</HubTitleXl>
            {transactions.length > 0 && (
              <CsvDownApi
                headers={csvHeaders}
                url={`${config.apis.societyPartnerHub.SETTLEMENT_LIST}`}
                params={{
                  pageNo: page,
                  pageSize: itemsPerPage,
                  orgUserDetailId,
                  voucherId: debouncedFilters.idFilter || null,
                }}
                method="post"
                reportName="SettlementTransactions.csv"
                title="Download CSV"
              />
            )}
          </HubFlexRow>

          <FilterWrap>
            <FilterComponent
              filterFields={[{ ...filterForm[0], value: filters.idFilter }]}
              onFilterChange={(val, name) => setFilters({ ...filters, [name]: val })}
              showButton={false}
            />
          </FilterWrap>

          <TransactionTable
            data={transactions}
            loading={loading}
            pagination={v2PaginationZeroBased({
              pageZeroBased: page,
              pageSize: itemsPerPage,
              total: totalRecords,
              setPageZeroBased: setPage,
              setPageSize: setItemsPerPage,
            })}
            onTransactionView={handleTransactionView}
          />
        </>
      ) : (
        <>
          <HubFlexRow $justify="space-between" $mb={theme.spacing.md}>
            <HubTitleLg>Transaction Details for {selectedTransaction?.transactionId}</HubTitleLg>
            {subTransactions.length > 0 && (
              <CsvDownApi
                headers={subCsvHeaders}
                url={`${config.apis.societyPartnerHub.PER_SETTLEMENT_TXN}`}
                params={{
                  pageNo: txnPage,
                  pageSize: itemsPerPageTxn,
                  voucherId: selectedTransaction.voucherId,
                }}
                method="post"
                reportName="PerTransactions.csv"
                title="Download CSV"
              />
            )}
          </HubFlexRow>

          <SubTransactionTable
            data={subTransactions}
            loading={loading}
            pagination={v2PaginationZeroBased({
              pageZeroBased: txnPage,
              pageSize: itemsPerPageTxn,
              total: totalRecordsTxn,
              setPageZeroBased: setTxnPage,
              setPageSize: setItemsPerPageTxn,
            })}
          />
        </>
      )}
    </HubPageShell>
  );
};

export default Transactions;
